// src/components/inputs/RichTextEditor.jsx
import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledEditorWrapper = styled(Box)(({ theme, isDark }) => ({
    '& .quill': {
        backgroundColor: isDark ? theme.palette.background.paper : theme.palette.background.input,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
    },
    '& .ql-toolbar': {
        backgroundColor: isDark ? theme.palette.background.default : '#f5f5f5',
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .ql-container': {
        backgroundColor: isDark ? theme.palette.background.paper : theme.palette.background.input,
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        fontSize: '14px',
        minHeight: '200px',
    },
    '& .ql-editor': {
        minHeight: '200px',
        color: theme.palette.text.primary,
        '&.ql-blank::before': {
            color: theme.palette.text.secondary,
            fontStyle: 'normal',
        },
    },
    '& .ql-stroke': {
        stroke: isDark ? theme.palette.text.secondary : theme.palette.text.primary,
    },
    '& .ql-fill': {
        fill: isDark ? theme.palette.text.secondary : theme.palette.text.primary,
    },
    '& .ql-picker-label': {
        color: isDark ? theme.palette.text.secondary : theme.palette.text.primary,
    },
    // ✅ إصلاح الـ Dropdown Menus
    '& .ql-picker-options': {
        backgroundColor: isDark ? theme.palette.background.paper : '#fff',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: '4px 0',
        boxShadow: isDark
            ? '0 4px 12px rgba(0, 0, 0, 0.5)'
            : '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    '& .ql-picker-item': {
        color: isDark ? theme.palette.text.primary : theme.palette.text.primary,
        '&:hover': {
            backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
        },
    },
    // ✅ إصلاح الأيقونات داخل الـ Dropdown
    '& .ql-picker-item .ql-stroke': {
        stroke: isDark ? theme.palette.text.primary : theme.palette.text.primary,
    },
    '& .ql-picker-item .ql-fill': {
        fill: isDark ? theme.palette.text.primary : theme.palette.text.primary,
    },
    // ✅ إصلاح الـ Color Picker
    '& .ql-color-picker .ql-picker-options': {
        width: '152px',
    },
    '& .ql-picker.ql-expanded .ql-picker-label': {
        borderColor: isDark ? theme.palette.divider : '#ccc',
    },
    // ✅ إصلاح الـ Active State
    '& .ql-toolbar button:hover, & .ql-toolbar button:focus': {
        color: theme.palette.primary.main,
        '& .ql-stroke': {
            stroke: theme.palette.primary.main,
        },
        '& .ql-fill': {
            fill: theme.palette.primary.main,
        },
    },
    '& .ql-toolbar button.ql-active': {
        color: theme.palette.primary.main,
        '& .ql-stroke': {
            stroke: theme.palette.primary.main,
        },
        '& .ql-fill': {
            fill: theme.palette.primary.main,
        },
    },
}));

const TextEditor = ({
    value,
    onChange,
    placeholder = 'Enter text...',
    name,
    minHeight = '200px'
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const modules = useMemo(
        () => ({
            toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }, { background: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ align: [] }],
                ['link', 'image'],
                ['clean'],
            ],
        }),
        []
    );

    const formats = [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'background',
        'list',
        'bullet',
        'indent',
        'align',
        'link',
        'image',
    ];

    const handleChange = (content) => {
        if (onChange) {
            const syntheticEvent = {
                target: {
                    name: name,
                    value: content,
                },
            };
            onChange(syntheticEvent);
        }
    };

    return (
        <StyledEditorWrapper isDark={isDark}>
            <ReactQuill
                theme="snow"
                value={value || ''}
                onChange={handleChange}
                modules={modules}
                formats={formats}
                placeholder={placeholder}
                style={{
                    minHeight: minHeight,
                }}
            />
        </StyledEditorWrapper>
    );
};

export default TextEditor;
